import { z } from 'zod';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Save, Send, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { P, PermissionGuard } from '@/features/permissions';
import { useCreatePatient } from '@/features/emr/api/patients-api';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// ─── Schema ──────────────────────────────────────────────────────

const patientFormSchema = z.object({
  phone: z.string().optional(),
  address: z.string().optional(),
  allergies: z.string().optional(),
  insurance_provider: z.string().optional(),
  insurance_group_number: z.string().optional(),
  emergency_contact_name: z.string().optional(),
  insurance_policy_number: z.string().optional(),
  emergency_contact_phone: z.string().optional(),
  last_name: z.string().min(1, 'Last name is required'),
  emergency_contact_relationship: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  medical_record_number: z.string().min(1, 'MRN is required'),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  gender: z.enum(['M', 'F', 'O'], { message: 'Gender is required' }),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
});

type PatientFormValues = z.infer<typeof patientFormSchema>;

// ─── Component ───────────────────────────────────────────────────

export function NewPatientPage() {
  return (
    <PermissionGuard permission={P.CREATE_PATIENT}>
      <NewPatientPageContent />
    </PermissionGuard>
  );
}

function NewPatientPageContent() {
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const formRef = useRef<HTMLFormElement>(null);
  const submitTypeRef = useRef<'draft' | 'submit'>('draft');
  const [isLoading, setIsLoading] = useState(false);
  const createPatient = useCreatePatient();

  const form = useForm<PatientFormValues>({
    resolver: zodResolver(patientFormSchema),
    defaultValues: {
      phone: '',
      email: '',
      address: '',
      last_name: '',
      allergies: '',
      first_name: '',
      date_of_birth: '',
      gender: undefined,
      insurance_provider: '',
      medical_record_number: '',
      insurance_group_number: '',
      emergency_contact_name: '',
      insurance_policy_number: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
    },
  });

  // ─── Header actions ──────────────────────────────────────────

  const handleCancel = () => {
    navigate({ to: '/patients' });
  };

  const handleSaveDraft = () => {
    submitTypeRef.current = 'draft';
    formRef.current?.requestSubmit();
  };

  const handleSubmit = () => {
    submitTypeRef.current = 'submit';
    formRef.current?.requestSubmit();
  };

  useEffect(() => {
    setPageHeader({
      title: 'Add New Patient',
      actions: (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            aria-label="Cancel"
            disabled={isLoading}
            className="hidden md:flex"
            onClick={handleCancel}
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={isLoading}
            aria-label="Save as Draft"
            onClick={handleSaveDraft}
          >
            {isLoading && submitTypeRef.current === 'draft' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="hidden md:inline">
              {isLoading && submitTypeRef.current === 'draft' ? 'Saving...' : 'Save Draft'}
            </span>
          </Button>
          <Button
            disabled={isLoading}
            aria-label="Submit Patient"
            onClick={handleSubmit}
          >
            {isLoading && submitTypeRef.current === 'submit' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span className="hidden md:inline">
              {isLoading && submitTypeRef.current === 'submit' ? 'Submitting...' : 'Submit'}
            </span>
          </Button>
        </div>
      ),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader, isLoading]);

  // ─── Form submission ─────────────────────────────────────────

  const onSubmit = async (values: PatientFormValues) => {
    setIsLoading(true);
    try {
      await createPatient.mutateAsync({
        medical_record_number: values.medical_record_number,
        patient_source: submitTypeRef.current === 'draft' ? 'draft' : 'manual',
        metadata_: {
          first_name: values.first_name,
          last_name: values.last_name,
          date_of_birth: values.date_of_birth,
          gender: values.gender,
          phone: values.phone || undefined,
          email: values.email || undefined,
          address: values.address || undefined,
          allergies: values.allergies
            ? values.allergies.split(',').map((a) => a.trim())
            : undefined,
          insurance: values.insurance_provider
            ? {
                provider: values.insurance_provider,
                policy_number: values.insurance_policy_number || '',
                group_number: values.insurance_group_number || undefined,
              }
            : undefined,
          emergency_contact: values.emergency_contact_name
            ? {
                name: values.emergency_contact_name,
                phone: values.emergency_contact_phone || '',
                relationship: values.emergency_contact_relationship || '',
              }
            : undefined,
        },
      });
      toast.success(
        submitTypeRef.current === 'draft'
          ? 'Patient saved as draft'
          : 'Patient created successfully',
      );
      navigate({ to: '/patients' });
    } catch {
      toast.error('Failed to create patient');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="relative mt-4 px-4">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-lg font-medium">
              {submitTypeRef.current === 'draft' ? 'Saving patient...' : 'Creating patient...'}
            </p>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-4xl">
        <Form {...form}>
          <form
            ref={formRef}
            className="space-y-6"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {/* ─── Section 1: Basic Information ───────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    name="first_name"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          First Name <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="last_name"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Last Name <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Smith" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    name="date_of_birth"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Date of Birth <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="medical_record_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Medical Record Number <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. MRN-001234" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Gender */}
                <FormField
                  name="gender"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Gender <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <ToggleGroup
                          type="single"
                          variant="outline"
                          value={field.value ?? ''}
                          onValueChange={(value) => {
                            if (value) field.onChange(value);
                          }}
                        >
                          <ToggleGroupItem
                            value="M"
                            className="data-[state=on]:bg-blue-100 data-[state=on]:text-blue-800"
                          >
                            Male
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="F"
                            className="data-[state=on]:bg-pink-100 data-[state=on]:text-pink-800"
                          >
                            Female
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="O"
                            className="data-[state=on]:bg-purple-100 data-[state=on]:text-purple-800"
                          >
                            Other
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* ─── Section 2: Contact Information ────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    name="phone"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. (555) 123-4567" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="email"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="e.g. john@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  name="address"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. 123 Main St, City, State 12345" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* ─── Section 3: Medical Information ────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Medical Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  name="allergies"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Allergies</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Penicillin, Sulfa drugs (comma-separated)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* ─── Section 4: Insurance ──────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Insurance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="insurance_provider"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Insurance Provider</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Blue Cross Blue Shield" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="insurance_policy_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Policy Number</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. POL-123456" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="insurance_group_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Group Number</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. GRP-789" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* ─── Section 5: Emergency Contact ──────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="emergency_contact_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Jane Smith" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="emergency_contact_phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Phone</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. (555) 987-6543" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="emergency_contact_relationship"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Relationship</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Spouse, Parent, Sibling" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </form>
        </Form>
      </div>
    </div>
  );
}
